// src/app/services/performance-series/source.ts
//
// The single data seam for Performance. Every consumer calls fetchSeries(brand)
// and gets a ValidatedEnvelope; nothing else in the UI knows or cares where the
// data came from. Two sources sit behind it, selected by a GLOBAL, admin-owned
// backend setting (app_config, NOT a frontend env var):
//
//   source = "fixture"  (default) -> synthetic local data
//   source = "api"                -> GET /analytics/series
//
// The mode is read from GET /analytics/series-source at runtime and cached; an
// admin flips it once in the Global Settings page and every client follows.
// Both paths run through the SAME validateEnvelope(), so the fixture is a
// faithful rehearsal and the live swap is a one-toggle change with identical
// safety. fetchSeries never throws: any failure degrades to a valid empty
// envelope with diagnostics.

import { validateEnvelope } from "./validate";
import { getFixtureEnvelope } from "./fixtures";
import { fetchPerfSeriesSource, type SeriesSourceMode } from "./source-setting-api";
import { apiFetch } from "@/api/api-fetch";
import { resolveBrandCustomer } from "@/app/services/brand/resolve-customer";
import type { ValidatedEnvelope } from "./types";

export type { SeriesSourceMode };

// ── Source-mode resolution (backend-owned, cached) ────────────────────────────
// The mode changes rarely (an admin toggles it), so cache it for the same short
// window as the series data and share one in-flight request. Any read failure
// degrades safely to "fixture" so the page always renders something.
const _MODE_TTL_MS = 60_000;
let _modeCache: { at: number; p: Promise<SeriesSourceMode> } | null = null;
// Last resolved mode, for synchronous readers (e.g. a useMemo that just needs
// the current value). Starts at the safe default until the first resolve lands.
let _lastMode: SeriesSourceMode = "fixture";

export function resolveSourceMode(): Promise<SeriesSourceMode> {
  const now = Date.now();
  if (_modeCache && now - _modeCache.at < _MODE_TTL_MS) return _modeCache.p;
  const p = fetchPerfSeriesSource()
    .then((m) => { _lastMode = m; return m; })
    .catch(() => "fixture" as SeriesSourceMode);
  _modeCache = { at: now, p };
  return p;
}

/** The last resolved source mode, synchronously. Defaults to "fixture" until
 *  resolveSourceMode() has completed at least once. Prefer useSeriesSourceMode()
 *  in components so they re-render when the real value arrives. */
export function getSeriesSourceModeCached(): SeriesSourceMode {
  return _lastMode;
}

// ── Short-lived cache (mirrors the legacy analytics series helpers) ───────────
const _TTL_MS = 60_000;
const _cache = new Map<string, { at: number; p: Promise<ValidatedEnvelope> }>();

if (typeof window !== "undefined") {
  window.addEventListener("analytics:refresh", () => {
    _cache.clear();
    _modeCache = null; // re-read the (possibly just-changed) source setting
  });
}

async function loadFixture(brand: string): Promise<ValidatedEnvelope> {
  // Fixture mode is a local PREVIEW source. A brand WITHOUT a fixture gets an
  // honest empty envelope, never another brand's synthetic data dressed up as
  // its own: seeing a different client's envelope under your brand name reads
  // as fabricated measurement, not as a preview.
  const raw = getFixtureEnvelope(brand);
  const validated = validateEnvelope(raw ?? {}, brand);
  if (!raw) {
    validated.diagnostics.push({ code: "fixture_missing", detail: `No fixture for brand "${brand}"` });
  }
  return validated;
}

async function loadApi(brand: string): Promise<ValidatedEnvelope> {
  try {
    // Ask by customer id, never by name. A brand string can answer for more
    // than one customer — an internal customer carrying a client's framework,
    // one client with several brands, one brand with several measurement
    // lineages — and the server's name lookup can only guess between them,
    // which is how another customer's runs reached this brand's page.
    //
    // A brand with no Deep Signal customer (monitored but never scored) has no
    // id to send, and neither does a brand whose lookup failed. Both fall back
    // to the name — the server's own lookup is exact-first now, so the fallback
    // cannot pick up an unrelated customer's runs either — and the envelope
    // records that it happened rather than reading as a clean id resolution.
    const customer = await resolveBrandCustomer(brand);
    const query = customer
      ? `customer_id=${customer.id}`
      : `brand=${encodeURIComponent(brand)}`;
    const raw = await apiFetch(`/analytics/series?${query}`);
    const validated = validateEnvelope(raw, brand);
    if (!customer) {
      validated.diagnostics.push({
        code: "resolved_by_brand_name",
        detail: `No Deep Signal customer resolved for "${brand}"; asked by name`,
      });
    }
    return validated;
  } catch (err) {
    // Network/auth/parse failure must not break the page: return an empty,
    // valid envelope and record why.
    const fallback = validateEnvelope({}, brand);
    fallback.diagnostics.push({ code: "api_fetch_failed", detail: String(err) });
    fallback.fellBack = true;
    return fallback;
  }
}

/**
 * Fetch a brand's full measurement set as a validated, normalized envelope.
 * The source mode is resolved from the backend setting first, then the matching
 * loader runs. Cached briefly per brand; cleared on the "analytics:refresh"
 * event (which also drops the cached source mode).
 */
export function fetchSeries(brand: string): Promise<ValidatedEnvelope> {
  const key = (brand || "").toLowerCase();
  const now = Date.now();
  const hit = _cache.get(key);
  if (hit && now - hit.at < _TTL_MS) return hit.p;

  const p = (async () => {
    const mode = await resolveSourceMode();
    return mode === "api" ? loadApi(brand) : loadFixture(brand);
  })().catch((err) => {
    _cache.delete(key);
    // Last-ditch guard: even an unexpected rejection resolves to empty.
    const fallback = validateEnvelope({}, brand);
    fallback.diagnostics.push({ code: "source_unexpected", detail: String(err) });
    fallback.fellBack = true;
    return fallback;
  });
  _cache.set(key, { at: now, p });
  return p;
}
